import { Controller } from "@hotwired/stimulus";

/**
 * Hero Entrance Controller
 *
 * Orchestrates a staggered entrance animation for hero section elements
 * using the Web Animations API with spring-like easing curves.
 *
 * After each element's entrance completes, inline animation styles are
 * cleaned up so CSS scroll-driven animations can take over without conflict.
 *
 * Respects prefers-reduced-motion — shows all content immediately.
 */
export default class extends Controller<HTMLElement> {
  static targets = [
    "subtitle",
    "title",
    "description",
    "cta",
    "trust",
    "codeBlock",
  ];

  declare readonly subtitleTarget: HTMLElement;
  declare readonly titleTarget: HTMLElement;
  declare readonly descriptionTarget: HTMLElement;
  declare readonly ctaTarget: HTMLElement;
  declare readonly trustTarget: HTMLElement;
  declare readonly codeBlockTarget: HTMLElement;

  connect(): void {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.showAll();
      return;
    }

    this.runEntrance();
  }

  private showAll(): void {
    [
      this.subtitleTarget,
      this.titleTarget,
      this.descriptionTarget,
      this.ctaTarget,
      this.trustTarget,
      this.codeBlockTarget,
    ].forEach((el) => el.classList.remove("hero-entrance-item"));
  }

  private runEntrance(): void {
    // Spring overshoot — elements arrive with momentum and settle
    const spring = "cubic-bezier(0.34, 1.56, 0.64, 1)";
    // Smooth deceleration — for smaller/subtler elements
    const soft = "cubic-bezier(0.25, 1, 0.5, 1)";

    const sequence: Array<{
      target: HTMLElement;
      delay: number;
      duration: number;
      translateY: number;
      easing: string;
      scale?: number;
    }> = [
      {
        target: this.subtitleTarget,
        delay: 80,
        duration: 400,
        translateY: 20,
        easing: soft,
      },
      {
        target: this.titleTarget,
        delay: 200,
        duration: 550,
        translateY: 30,
        easing: spring,
        scale: 0.97,
      },
      {
        target: this.descriptionTarget,
        delay: 380,
        duration: 350,
        translateY: 15,
        easing: soft,
      },
      {
        target: this.ctaTarget,
        delay: 520,
        duration: 400,
        translateY: 15,
        easing: spring,
        scale: 0.95,
      },
      {
        target: this.trustTarget,
        delay: 660,
        duration: 300,
        translateY: 10,
        easing: soft,
      },
      {
        target: this.codeBlockTarget,
        delay: 800,
        duration: 550,
        translateY: 40,
        easing: spring,
      },
    ];

    for (const item of sequence) {
      const fromTransform = item.scale
        ? `translateY(${item.translateY}px) scale(${item.scale})`
        : `translateY(${item.translateY}px)`;

      const animation = item.target.animate(
        [
          { opacity: 0, transform: fromTransform },
          { opacity: 1, transform: "translateY(0) scale(1)" },
        ],
        {
          delay: item.delay,
          duration: item.duration,
          easing: item.easing,
          fill: "forwards",
        }
      );

      // Clean up after animation so CSS scroll-driven animations can take over
      animation.finished.then(() => {
        item.target.classList.remove("hero-entrance-item");
        animation.cancel();
      });
    }
  }
}
